import { useState, useEffect, useCallback, useMemo, createContext, useContext } from 'react';
import { api } from './api';
import {
    getTodayStr,
    getCompletionsForDate,
    calculateStreak,
    calculateBestStreak,
    get7DayHistory,
    FREE_TIER_STACK_LIMIT,
} from './store';

// Auth Context
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Check for existing session on mount
    useEffect(() => {
        const token = api.getToken();
        if (token) {
            api.getMe()
                .then(user => setUser(user))
                .catch(() => {
                    api.logout();
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        setError(null);
        try {
            const { user } = await api.login(email, password);
            setUser(user);
            return user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const register = async (email, password, name) => {
        setError(null);
        try {
            const response = await api.register(email, password, name);
            // Don't set user - they need to verify email first
            return response;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const verifyEmail = async (token) => {
        setError(null);
        try {
            const { user } = await api.verifyEmail(token);
            setUser(user);
            return user;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const logout = () => {
        api.logout();
        setUser(null);
    };

    const forgotPassword = async (email) => {
        setError(null);
        try {
            return await api.forgotPassword(email);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    const resetPassword = async (token, password) => {
        setError(null);
        try {
            return await api.resetPassword(token, password);
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, error, login, register, verifyEmail, logout, forgotPassword, resetPassword }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}

// Stacks Hook
export function useStacks() {
    const { user } = useAuth();
    const [stacks, setStacks] = useState([]);
    const [completions, setCompletions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Load data
    const loadData = useCallback(async () => {
        if (!user) {
            setStacks([]);
            setCompletions([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const syncData = await api.sync();
            setStacks(syncData.stacks || []);
            // Normalize completion dates
            const normalizedCompletions = (syncData.completions || []).map(c => ({
                ...c,
                completed_at: typeof c.completed_at === 'string' 
                    ? c.completed_at 
                    : new Date(c.completed_at).toISOString()
            }));
            setCompletions(normalizedCompletions);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [user]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const activeStacks = useMemo(() => {
        return stacks
            .filter(s => !s.archived_at)
            .sort((a, b) => a.position - b.position);
    }, [stacks]);

    const archivedStacks = useMemo(() => {
        return stacks.filter(s => s.archived_at);
    }, [stacks]);

    const todayStr = getTodayStr();

    const getStackProgress = useCallback((stackId) => {
        const stack = stacks.find(s => s.id === stackId);
        if (!stack) return null;

        // Ensure items array exists
        const items = stack.items || [];
        const todayCompletions = getCompletionsForDate(completions, stackId, todayStr);
        const completedItemIds = new Set(todayCompletions.map(c => c.item_id));

        return {
            ...stack,
            items, // Ensure items is always an array
            completedToday: todayCompletions.length,
            totalItems: items.length,
            isComplete: items.length > 0 && todayCompletions.length >= items.length,
            completedItemIds,
            streak: calculateStreak(completions, stack),
            bestStreak: calculateBestStreak(completions, stack),
            history7Day: get7DayHistory(completions, stack),
        };
    }, [stacks, completions, todayStr]);

    const completeItem = useCallback(async (stackId, itemId) => {
        try {
            const completion = await api.createCompletion(stackId, itemId);
            const normalizedCompletion = {
                ...completion,
                completed_at: typeof completion.completed_at === 'string'
                    ? completion.completed_at
                    : new Date(completion.completed_at).toISOString()
            };
            setCompletions(prev => [...prev, normalizedCompletion]);
            return completion.id;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteCompletion = useCallback(async (completionId) => {
        try {
            await api.deleteCompletion(completionId);
            setCompletions(prev => prev.filter(c => c.id !== completionId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const undoLastCompletion = useCallback(async (stackId) => {
        const todayCompletions = getCompletionsForDate(completions, stackId, todayStr)
            .sort((a, b) => new Date(b.completed_at) - new Date(a.completed_at));

        if (todayCompletions.length > 0) {
            await deleteCompletion(todayCompletions[0].id);
            return todayCompletions[0].item_id;
        }
        return null;
    }, [completions, todayStr, deleteCompletion]);

    const createStack = useCallback(async (stackData) => {
        try {
            const newStack = await api.createStack(stackData);
            setStacks(prev => [...prev, newStack]);
            return newStack;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const updateStack = useCallback(async (stackId, updates) => {
        try {
            const updatedStack = await api.updateStack(stackId, updates);
            setStacks(prev => prev.map(s => s.id === stackId ? updatedStack : s));
            return updatedStack;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const archiveStack = useCallback(async (stackId) => {
        try {
            const updatedStack = await api.archiveStack(stackId);
            setStacks(prev => prev.map(s => s.id === stackId ? updatedStack : s));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const restoreStack = useCallback(async (stackId) => {
        try {
            const updatedStack = await api.restoreStack(stackId);
            setStacks(prev => prev.map(s => s.id === stackId ? updatedStack : s));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const deleteStack = useCallback(async (stackId) => {
        try {
            await api.deleteStack(stackId);
            setStacks(prev => prev.filter(s => s.id !== stackId));
            setCompletions(prev => prev.filter(c => c.stack_id !== stackId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    }, []);

    const getHistoryForMonth = useCallback((year, month) => {
        const history = {};
        const startDate = new Date(year, month, 1);
        const endDate = new Date(year, month + 1, 0);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateStr = d.toISOString().split('T')[0];
            const dayCompletions = completions.filter(c => c.completed_at.startsWith(dateStr));

            const stacksRan = new Set();
            dayCompletions.forEach(c => stacksRan.add(c.stack_id));

            history[dateStr] = {
                totalItems: dayCompletions.length,
                stacksRan: stacksRan.size,
                completions: dayCompletions,
            };
        }

        return history;
    }, [completions]);

    const isPro = user?.is_pro || false;
    const canCreateStack = isPro || activeStacks.length < FREE_TIER_STACK_LIMIT;

    return {
        stacks,
        activeStacks,
        archivedStacks,
        completions,
        isLoading,
        error,
        isPro,
        canCreateStack,
        freeTierLimit: FREE_TIER_STACK_LIMIT,
        getStackProgress,
        completeItem,
        deleteCompletion,
        undoLastCompletion,
        createStack,
        updateStack,
        archiveStack,
        restoreStack,
        deleteStack,
        getHistoryForMonth,
        refresh: loadData,
    };
}
